import {Injectable} from '@angular/core';
import {
  deepEqual,
  getDateWithoutOffset,
  isNonNull,
  stringToDate,
  TranslationService,
} from '@emma-labs/shared/angular';
import {
  calculateAgeTruncated,
  CategoryCode,
  diff,
  extractCoverageFromProtection,
  Fna,
  getTotalCoverage,
  JOB_CATEGORY_ID_TO_DESCRIPTION,
  Language,
  Proposal,
  ProviderId,
} from '@emma-labs/shared/typescript';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  first,
  forkJoin,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {map} from 'rxjs/operators';
import {Client} from '../../entities/proposal-page-object/client';
import {ProposalPageObject} from '../../entities/proposal-page-object/proposal-page-object';
import {BrokerProfileService} from '../broker-profile/broker-profile.service';
import {FnaService} from '../fna/fna.service';
import {HealthQuestionnaireService} from '../health-questionnaire.service';
import {
  buildProposalPageObject,
  getHealthAnswersFromClient,
  getSubmitterFromClient,
} from '../orchestrator-utils';
import {ProtectionRecommendationService} from '../protection/protection-recommendation.service';
import {SaleReportService} from '../sale/sale-report.service';
import {ProposalService} from './proposal.service';

@Injectable({
  providedIn: 'root',
})
export class ProposalPageOrchestrator {
  private readonly _store$: BehaviorSubject<ProposalPageObject | undefined>;
  private readonly store$: Observable<ProposalPageObject>;

  constructor(
    private readonly proposalService: ProposalService,
    private readonly fnaService: FnaService,
    private readonly healthQuestionnaireService: HealthQuestionnaireService,
    private readonly translate: TranslationService,
    private readonly saleReportService: SaleReportService,
    private readonly protectionRecommendationService: ProtectionRecommendationService,
    private readonly profileService: BrokerProfileService,
  ) {
    this._store$ = new BehaviorSubject<ProposalPageObject | undefined>(undefined);
    this.store$ = this._store$.pipe(filter(isNonNull), distinctUntilChanged(deepEqual));
  }

  get(proposalId: string): Observable<ProposalPageObject> {
    const currentValue = this._store$.getValue();

    if (!currentValue || currentValue.proposalId !== proposalId) {
      this._store$.next(undefined);
      this.fetch(proposalId);
    }

    return this.store$.pipe(
      tap((proposalPageObject) => this.getAndSaveRecommendedProtectionIfConditionsMet(proposalPageObject)),
    );
  }

  create(providerId: ProviderId, client: Client): Observable<ProposalPageObject> {
    const fna: Fna = {province: client.province!};

    return this.fnaService.create(fna).pipe(
      first(),
      switchMap((fna) =>
        forkJoin([
          this.proposalService.create(
            providerId,
            fna.id!,
            getSubmitterFromClient(client),
            getHealthAnswersFromClient(client),
          ),
          of(fna),
        ]),
      ),
      switchMap(([proposal, fna]) =>
        forkJoin([
          of(proposal),
          of(fna),
          this.healthQuestionnaireService.getQuestions(
            proposal.providerId,
            proposal.providerVersion.healthQuestionnaireVersion,
            proposal.healthAnswers!,
          ),
          this.profileService.getPublicProfile(proposal.agents!.find((agent) => agent.isPrimary)!.agentId),
        ]),
      ),
      map(([proposal, fna, healthQuestionnaire, primaryAgent]) =>
        buildProposalPageObject(this.translate.currentLanguageIsFr(), {
          proposal,
          fna,
          healthQuestionnaire,
          primaryAgent,
        }),
      ),
      tap((obj) => this._store$.next(obj)),
    );
  }

  update(proposalPageObject: Partial<ProposalPageObject>): Observable<ProposalPageObject> {
    const storeValue = this._store$.getValue();
    if (!storeValue) {
      throw new Error('Cannot update a proposal that was never fetched.');
    }

    const proposalId = storeValue.proposalId!;
    // ProposalPageObject works in sections, so we can perform the update for a particular section if it is defined
    if (proposalPageObject.client) {
      return this.updateFromClient(proposalId, proposalPageObject.client);
    }
    if (proposalPageObject.fna) {
      const observables$: Observable<any>[] = [
        this.fnaService
          .update(storeValue.fna!.id!, {
            ...proposalPageObject.fna,
            job: proposalPageObject.fna!.jobCategoryId
              ? JOB_CATEGORY_ID_TO_DESCRIPTION[proposalPageObject.fna!.jobCategoryId][
                  this._store$.getValue()?.client?.preferredLanguage === Language.FRENCH ? 'fr' : 'en'
                ]
              : proposalPageObject.fna!.job,
            province: storeValue.client!.province,
          })
          .pipe(first()),
      ];

      if (proposalPageObject.fna!.jobCategoryId) {
        observables$.push(
          this.proposalService
            .updateJobCategoryId(storeValue.proposalId!, proposalPageObject.fna!.jobCategoryId)
            .pipe(first()),
        );
      }

      return forkJoin(observables$).pipe(
        map(([fna, proposal]) =>
          buildProposalPageObject(
            this.translate.currentLanguageIsFr(),
            {fna, proposal},
            this._store$.getValue()!,
          ),
        ),
        tap((obj) => {
          this._store$.next(obj);
        }),
      );
    }
    if (proposalPageObject.healthAnswers) {
      const healthAnswers = proposalPageObject.healthAnswers;
      return this.updateProposal(
        proposalId,
        (id) => this.proposalService.updateHealthAnswers(id, healthAnswers),
        true,
      );
    }
    if (proposalPageObject.coverage) {
      const coverage = proposalPageObject.coverage;
      return this.updateProposal(proposalId, (id) => this.proposalService.updateProtection(id, coverage));
    }
    if (proposalPageObject.beneficiaries) {
      return this.updateProposal(proposalId, (id) =>
        this.proposalService.updateBeneficiaries(id, proposalPageObject.beneficiaries!),
      );
    }
    if (proposalPageObject.replacementNotice) {
      return this.updateProposal(proposalId, (id) =>
        proposalPageObject.replacementNotice?.hasPolicyReplacementNotice
          ? this.proposalService.addReplacementNotice(id, proposalPageObject.replacementNotice!.file!)
          : this.proposalService.removeReplacementNotice(id),
      );
    }
    if (proposalPageObject.address) {
      return this.updateProposal(proposalId, (id) =>
        this.proposalService.updateSubmitter(id, {...proposalPageObject.address}),
      );
    }
    if (proposalPageObject.idDocument) {
      return this.updateProposal(proposalId, (id) =>
        this.proposalService.updateIdDocument(id, proposalPageObject.idDocument!),
      );
    }
    if (proposalPageObject.disclosures) {
      return this.updateProposal(proposalId, (id) =>
        this.proposalService.updateAgentDisclosures(
          id,
          proposalPageObject.disclosures!.clientAgentAffiliationTime,
          proposalPageObject.disclosures!.isAccepted,
        ),
      );
    }
    // This condition is to allow empty string
    if (proposalPageObject.note != null) {
      return this.updateProposal(proposalId, (id) =>
        this.proposalService.updateNote(
          id,
          proposalPageObject.note !== '' ? proposalPageObject.note : undefined,
        ),
      );
    }
    throw new Error('Invalid update');
  }

  resetProtection(): Observable<void> {
    return this.store$.pipe(
      first(),
      tap((proposalPageObject) => {
        if (!proposalPageObject || !proposalPageObject.protection) {
          throw new Error('Cannot reset protection if one is not already saved.');
        }

        this.getAndSaveRecommendedProtectionIfConditionsMet({...proposalPageObject, protection: undefined});
      }),
      map(() => undefined),
    );
  }

  send(): Observable<void> {
    const storeValue = this._store$.getValue();
    if (!storeValue) {
      throw new Error('Cannot send a proposal that was never fetched.');
    }

    return this.proposalService.send(storeValue.proposalId!).pipe(
      first(),
      tap((proposal) => {
        this._store$.next(
          buildProposalPageObject(this.translate.currentLanguageIsFr(), {proposal}, this._store$.getValue()!),
        );
      }),
      map(() => undefined),
    );
  }

  delete(): Observable<void> {
    const storeValue = this._store$.getValue();
    if (!storeValue) {
      throw new Error('Cannot delete a proposal that was never fetched.');
    }

    return this.proposalService.delete(storeValue.proposalId!);
  }

  copyToBrokerage(): Observable<void> {
    const storeValue = this._store$.getValue();
    if (!storeValue) {
      throw new Error('Cannot copy to brokerage a proposal that was never fetched.');
    }

    return this.proposalService.copyToBrokerage(storeValue.proposalId!);
  }

  setCurrentProtectionSearchResult(policyNumber: string): Observable<void> {
    return forkJoin({
      proposal: this.proposalService.get(this.getProposalId()),
      saleReports: this.saleReportService.search(policyNumber, true),
    }).pipe(
      map(({proposal, saleReports}) => ({
        proposalPageObject: buildProposalPageObject(
          this.translate.currentLanguageIsFr(),
          {proposal},
          this._store$.getValue()!,
        ),
        saleReports,
      })),
      tap(({proposalPageObject, saleReports}) => {
        proposalPageObject.fna!.protectionSearchResult = undefined;
        this._store$.next(proposalPageObject);
        let protectionSearchResult;

        if (saleReports && saleReports.length > 0) {
          const matchingSaleReport = saleReports.find(
            (saleReport) => saleReport.policyNumber === policyNumber,
          );
          if (matchingSaleReport) {
            protectionSearchResult = {
              coverageAmount: getTotalCoverage(matchingSaleReport.protection.personInsurances),
              issueDate: matchingSaleReport.createdAt,
            };
          }
        }
        proposalPageObject.fna!.protectionSearchResult = protectionSearchResult;

        this._store$.next(proposalPageObject);

        if (!proposalPageObject.fna!.protectionSearchResult) {
          throw new Error('Policy number not found in sale reports');
        }
      }),
      map(() => undefined),
    );
  }

  private updateFromClient(proposalId: string, client: Client): Observable<ProposalPageObject> {
    const diffClient: Client = diff(this._store$.getValue()!.client!, client);

    // The date in the store comes from the backend and is not deserialized into a Date object
    let currBirthday = this._store$.getValue()?.client?.birthday;
    if (typeof currBirthday === 'string') {
      currBirthday = stringToDate(currBirthday);
    }
    // Due to timezone differences, the birthday might be off by a day while still being the same UTC date. We ignore these cases.
    if (
      diffClient.birthday &&
      currBirthday &&
      getDateWithoutOffset(diffClient.birthday).getTime() === currBirthday.getTime()
    ) {
      delete diffClient.birthday;
    }

    let proposal$: Observable<Proposal> | undefined;
    if (diffClient.birthday || diffClient.sex) {
      proposal$ = this.proposalService.updateHealthAnswers(
        proposalId,
        getHealthAnswersFromClient(diffClient),
      );
    }

    if (Object.keys(diffClient).some((key) => key !== 'sex' && key !== 'birthday')) {
      const observables$: Observable<unknown>[] = [
        this.proposalService.updateSubmitter(proposalId, getSubmitterFromClient(diffClient)),
      ];
      if (diffClient.province) {
        const storeFna = this._store$.getValue()!.fna!;
        observables$.push(this.fnaService.update(storeFna.id!, {...storeFna, province: diffClient.province}));
      }

      if (proposal$) {
        proposal$ = proposal$.pipe(
          switchMap(() => forkJoin(observables$)),
          map(([proposal, _]) => proposal),
        ) as Observable<Proposal>;
      } else {
        proposal$ = forkJoin(observables$).pipe(map(([proposal, _]) => proposal)) as Observable<Proposal>;
      }
    }

    return proposal$
      ? proposal$.pipe(
          switchMap((proposal) =>
            forkJoin([
              of(proposal),
              this.fnaService.get(this._store$.getValue()!.fna!.id!),
              this.healthQuestionnaireService.getQuestions(
                proposal.providerId,
                proposal.providerVersion.healthQuestionnaireVersion,
                proposal.healthAnswers!,
              ),
            ]),
          ),
          map(([proposal, fna, healthQuestionnaire]) =>
            buildProposalPageObject(
              this.translate.currentLanguageIsFr(),
              {proposal, fna, healthQuestionnaire},
              this._store$.getValue()!,
            ),
          ),
          tap((obj) => this._store$.next(obj)),
        )
      : of(this._store$.getValue()!);
  }

  private updateProposal(
    proposalId: string,
    updateFn: (proposalId: string) => Observable<Proposal>,
    shouldFetchHealthQuestionnaire = false,
  ): Observable<ProposalPageObject> {
    const proposal$: Observable<Proposal> = updateFn(proposalId).pipe(first());
    let proposalPageObject$: Observable<ProposalPageObject>;

    if (shouldFetchHealthQuestionnaire) {
      proposalPageObject$ = proposal$.pipe(
        switchMap((proposal) =>
          forkJoin([
            of(proposal),
            this.healthQuestionnaireService.getQuestions(
              proposal.providerId,
              proposal.providerVersion.healthQuestionnaireVersion,
              proposal.healthAnswers!,
            ),
          ]),
        ),
        map(([proposal, healthQuestionnaire]) =>
          buildProposalPageObject(
            this.translate.currentLanguageIsFr(),
            {
              proposal,
              healthQuestionnaire,
            },
            this._store$.getValue()!,
          ),
        ),
      );
    } else {
      proposalPageObject$ = proposal$.pipe(
        map((proposal) =>
          buildProposalPageObject(
            this.translate.currentLanguageIsFr(),
            {
              proposal,
            },
            this._store$.getValue()!,
          ),
        ),
      );
    }

    return proposalPageObject$.pipe(tap((obj) => this._store$.next(obj)));
  }

  private getProposalId(): string {
    const storeValue = this._store$.getValue();
    if (!storeValue || !storeValue.proposalId) {
      throw new Error('Cannot get a proposalId that was never fetched.');
    }

    return storeValue.proposalId;
  }

  private fetch(proposalId: string): void {
    this.proposalService
      .get(proposalId)
      .pipe(
        first(),
        switchMap((proposal) =>
          forkJoin([
            of(proposal),
            this.fnaService.get(proposal.fnaId!),
            this.healthQuestionnaireService.getQuestions(
              proposal.providerId,
              proposal.providerVersion.healthQuestionnaireVersion,
              proposal.healthAnswers ?? {},
            ),
            this.profileService.getPublicProfile(proposal.agents!.find((agent) => agent.isPrimary)!.agentId),
          ]),
        ),
      )
      .subscribe(([proposal, fna, healthQuestionnaire, primaryAgent]) => {
        this._store$.next(
          buildProposalPageObject(this.translate.currentLanguageIsFr(), {
            proposal,
            fna,
            healthQuestionnaire,
            primaryAgent,
          }),
        );
      });
  }

  private getAndSaveRecommendedProtectionIfConditionsMet(proposalPageObject: ProposalPageObject): void {
    const {fna, healthQuestionnaire, healthAnswers, protection} = proposalPageObject;

    if (
      !protection &&
      fna?.recommendationNeeds != null &&
      fna.recommendationTemporaryNeeds != null &&
      fna.recommendationPermanentNeeds != null &&
      fna.cancerNeeds != null &&
      fna.salary != null &&
      healthAnswers?.smoker != null &&
      healthQuestionnaire?.categoryCode &&
      healthQuestionnaire.categoryCode !== CategoryCode.INELIGIBLE &&
      healthAnswers.birthday &&
      healthAnswers.sex &&
      proposalPageObject.ratesVersion
    ) {
      this.protectionRecommendationService
        .getRecommendedProtection(
          proposalPageObject.ratesVersion,
          ProviderId.HUMANIA_EMMA_V2,
          healthAnswers.sex,
          healthAnswers.smoker,
          calculateAgeTruncated(new Date(healthAnswers.birthday)),
          fna.recommendationTemporaryNeeds,
          fna.recommendationPermanentNeeds,
          fna.recommendationNeeds,
          fna.cancerNeeds,
          fna.salary,
          fna.numChildren ?? 0,
          healthQuestionnaire.categoryCode,
          fna.youngestChildAgeInMonths ? Math.floor(fna.youngestChildAgeInMonths / 12) : undefined,
        )
        .pipe(
          switchMap((recommendedProtection) => {
            const coverage = extractCoverageFromProtection(recommendedProtection);
            return this.update({coverage});
          }),
          first(),
        )
        .subscribe();
    }
  }
}
